import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from '../features/auth/contexts/useAuth';
import { SkeletonStyles } from '../shared/components/ui/SkeletonLoader';
import { Layout } from '../shared/components/layout/Layout';
import { ProtectedRoute } from '../features/auth/components/ProtectedRoute';
import { PublicOnlyRoute } from '../features/auth/components/PublicOnlyRoute';
import { LoadingState } from '../shared/components/ui/LoadingState';
import { ErrorBoundary } from '../shared/components/ErrorBoundary';

// Public pages
import { Home } from '../shared/pages/public/Home';
import { About } from '../shared/pages/public/About';
import { Contact } from '../shared/pages/public/Contact';
import { Blog } from '../shared/pages/public/Blog';
import { FAQ } from '../shared/pages/public/FAQ';
import { Terms } from '../shared/pages/public/Terms';
import { Privacy } from '../shared/pages/public/Privacy';
import { Cancellation } from '../shared/pages/public/Cancellation';
import { UnauthorizedPage } from '../shared/pages/public/UnauthorizedPage';

// Auth pages
import { LoginForm } from '../features/auth/components/LoginForm';
import { RegisterForm } from '../features/auth/forms/RegisterForm';
const ForgotPassword = React.lazy(() =>
  import('../features/auth/pages/ForgotPassword').then(module => ({
    default: module.default,
  }))
);

// Feature pages - Lazy loaded to improve initial load time
const Dashboard = React.lazy(() =>
  import('../features/dashboard/pages/Dashboard').then(module => ({
    default: module.Dashboard,
  }))
);
const Profile = React.lazy(() =>
  import('../features/profile/pages/Profile').then(module => ({
    default: module.Profile,
  }))
);
const Company = React.lazy(() =>
  import('../features/profile/pages/Company').then(module => ({
    default: module.Company,
  }))
);
const Account = React.lazy(() =>
  import('../features/account/pages/Account').then(module => ({
    default: module.Account,
  }))
);

const Bookings = React.lazy(() =>
  import('../features/bookings/pages/Bookings').then(module => ({
    default: module.Bookings,
  }))
);
const BookingDetail = React.lazy(() =>
  import('../features/bookings/pages/BookingDetail').then(module => ({
    default: module.BookingDetail,
  }))
);
const SafariSearch = React.lazy(() =>
  import('../features/safaris/pages/SafariSearch').then(module => ({
    default: module.SafariSearch,
  }))
);
const SafariDetails = React.lazy(() =>
  import('../features/safaris/pages/SafariDetails').then(module => ({
    default: module.SafariDetails,
  }))
);
const TripSearch = React.lazy(() =>
  import('../features/trips/pages/TripSearch').then(module => ({
    default: module.TripSearch,
  }))
);
const TripDiscovery = React.lazy(() =>
  import('../features/trips/pages/TripDiscovery').then(module => ({
    default: module.TripDiscovery,
  }))
);
const TripDetails = React.lazy(() =>
  import('../features/trips/pages/TripDetails').then(module => ({
    default: module.TripDetails,
  }))
);
const BookingConfirmation = React.lazy(() =>
  import('../features/bookings/pages/BookingConfirmation').then(module => ({
    default: module.BookingConfirmation,
  }))
);
const BookingSuccess = React.lazy(() =>
  import('../features/bookings/pages/BookingSuccess').then(module => ({
    default: module.BookingSuccess,
  }))
);
const VehicleManagement = React.lazy(() =>
  import('../features/misc/pages/VehicleManagement').then(module => ({
    default: module.VehicleManagement,
  }))
);
const VerificationRequired = React.lazy(() =>
  import('../features/auth/components/VerificationRequired').then(module => ({
    default: module.VerificationRequired,
  }))
);
const SavedSafaris = React.lazy(() =>
  import('../features/safaris/pages/SavedSafaris').then(module => ({
    default: module.SavedSafaris,
  }))
);
const Reviews = React.lazy(() =>
  import('../features/reviews/pages/Reviews').then(module => ({
    default: module.Reviews,
  }))
);
const Deals = React.lazy(() =>
  import('../features/misc/pages/Deals').then(module => ({
    default: module.Deals,
  }))
);
const Payments = React.lazy(() =>
  import('../features/payments/pages/Payments').then(module => ({
    default: module.Payments,
  }))
);

const Notifications = React.lazy(() =>
  import('../features/notifications/pages/NotificationsPage').then(module => ({
    default: module.NotificationsPage,
  }))
);

const TripsPage = React.lazy(() =>
  import('../features/trips/pages/TripsPage').then(module => ({
    default: module.TripsPage,
  }))
);
const Fleet = React.lazy(() =>
  import('../features/fleet/pages/Fleet').then(module => ({
    default: module.Fleet,
  }))
);
const Safaris = React.lazy(() =>
  import('../features/safaris/pages/Safaris').then(module => ({
    default: module.Safaris,
  }))
);

function App() {
  const { loading } = useAuth();

  // Show loading screen while auth is initializing
  if (loading) {
    return <LoadingState message="Loading application..." />;
  }

  return (
    <ErrorBoundary>
      <Layout>
        <Suspense fallback={<LoadingState message="Loading page..." />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cancellation" element={<Cancellation />} />
            <Route
              path="/login"
              element={
                <PublicOnlyRoute>
                  <LoginForm />
                </PublicOnlyRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicOnlyRoute>
                  <ForgotPassword />
                </PublicOnlyRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicOnlyRoute>
                  <RegisterForm />
                </PublicOnlyRoute>
              }
            />
            <Route path="/safaris-search" element={<SafariSearch />} />
            <Route path="/safaris/:id" element={<SafariDetails />} />
            <Route path="/transport-search" element={<TripSearch />} />
            <Route path="/trips-discovery" element={<TripDiscovery />} />
            <Route path="/trips/:id" element={<TripDetails />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings"
              element={
                <ProtectedRoute>
                  <Bookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings/:id"
              element={
                <ProtectedRoute>
                  <BookingDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              }
            />
            <Route
              path="/company"
              element={
                <ProtectedRoute requiredRole="operator">
                  <Company />
                </ProtectedRoute>
              }
            />
            <Route
              path="/saved"
              element={
                <ProtectedRoute>
                  <SavedSafaris />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reviews"
              element={
                <ProtectedRoute>
                  <Reviews />
                </ProtectedRoute>
              }
            />
            <Route
              path="/deals"
              element={
                <ProtectedRoute>
                  <Deals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute>
                  <Payments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trips"
              element={
                <ProtectedRoute>
                  <TripsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fleet"
              element={
                <ProtectedRoute
                  requiredRole="operator"
                  requiredOperatorType="transporter"
                >
                  <Fleet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fleet/drivers"
              element={
                <ProtectedRoute
                  requiredRole="operator"
                  requiredOperatorType="transporter"
                >
                  <Fleet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fleet/validation"
              element={
                <ProtectedRoute
                  requiredRole="operator"
                  requiredOperatorType="transporter"
                >
                  <Fleet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/safaris-management"
              element={
                <ProtectedRoute
                  requiredRole="operator"
                  requiredOperatorType="tour_operator"
                >
                  <Safaris />
                </ProtectedRoute>
              }
            />
            <Route
              path="/booking/confirm"
              element={
                <ProtectedRoute>
                  <BookingConfirmation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/booking/success"
              element={
                <ProtectedRoute>
                  <BookingSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vehicles"
              element={
                <ProtectedRoute
                  requiredRole="operator"
                  requiredOperatorType="transporter"
                >
                  <VehicleManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/verification-required"
              element={
                <ProtectedRoute>
                  <VerificationRequired />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </Layout>

      <Toaster 
        position="bottom-center"
        toastOptions={{
          style: {
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(229, 229, 229, 0.8)',
            borderRadius: '12px',
            boxShadow: '0 4px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            padding: '12px 16px',
            margin: '16px',
            maxWidth: 'calc(100vw - 32px)',
            width: 'fit-content',
            minWidth: '300px',
            color: '#171717',
            fontSize: '14px',
            fontWeight: '500',
          },
          duration: 4000,
        }}
      />
      <SkeletonStyles />
    </ErrorBoundary>
  );
}

export default App;
